import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import allRoutes from 'client/constants/allRoutes';

const SubscriptionContext = createContext({
  isExpired: false,
  isTrialExpired: false,
  isGracePeriod: false,
  graceDaysLeft: 0,
  isAccountLocked: false,
  isNoSubscription: false,
  message: '',
  resetSubscriptionState: () => {},
});

export const useSubscription = () => useContext(SubscriptionContext);

const ALLOWED_PATHS = [
  allRoutes.planCheckout,
  allRoutes.profile,
  allRoutes.profileAccount,
  allRoutes.settings,
];

const SubscriptionProvider = ({ children }) => {
  const [state, setState] = useState({
    isExpired: false,
    isTrialExpired: false,
    isGracePeriod: false,
    graceDaysLeft: 0,
    isAccountLocked: false,
    isNoSubscription: false,
    message: '',
  });

  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((s) => s.auth);

  // Super Admin (RoleId 1) never gets blocked
  const isSuperAdmin = user?.RoleId === 1;

  const isAllowedPath = ALLOWED_PATHS.some((p) => location.pathname.startsWith(p));

  const handleSubscriptionExpired = useCallback((event) => {
    const { no_subscription, subscription_expired, trial_expired, grace_period, grace_days_left, account_locked, message } = event.detail;
    setState({
      isExpired: subscription_expired || false,
      isTrialExpired: trial_expired || false,
      isGracePeriod: grace_period || false,
      graceDaysLeft: grace_days_left || 0,
      isAccountLocked: account_locked || false,
      isNoSubscription: no_subscription || false,
      message: message || '',
    });
  }, []);

  // Reset state after successful payment / plan activation
  const resetSubscriptionState = useCallback(() => {
    setState({ isExpired: false, isTrialExpired: false, isGracePeriod: false, graceDaysLeft: 0, isAccountLocked: false, isNoSubscription: false, message: '' });
  }, []);

  useEffect(() => {
    window.addEventListener('subscription:expired', handleSubscriptionExpired);
    return () => window.removeEventListener('subscription:expired', handleSubscriptionExpired);
  }, [handleSubscriptionExpired]);

  // Redirect when expired/grace/locked/no-subscription and not on an allowed page
  useEffect(() => {
    if ((state.isExpired || state.isTrialExpired || state.isAccountLocked || state.isGracePeriod || state.isNoSubscription) && !isSuperAdmin && !isAllowedPath) {
      navigate(allRoutes.planCheckout, { replace: true });
    }
  }, [state.isExpired, state.isTrialExpired, state.isAccountLocked, state.isGracePeriod, state.isNoSubscription, isSuperAdmin, isAllowedPath, navigate]);

  return (
    <SubscriptionContext.Provider value={{ ...state, resetSubscriptionState }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionProvider;
